import { getFirebaseDb } from '@/services/firebaseService'
import {
  doc,
  getDoc,
  getDocs,
  collection,
  query,
  where,
  Timestamp,
  setDoc,
} from 'firebase/firestore'
import type { BankTransaction } from '@farmfree/common/types/bank/bankTransaction'
import type { ApiResponse } from '@farmfree/common/types/apiResponse'
import { updateCompanyVersion } from '@/utils/version/updateCompanyVersion'
import { toDate } from '@farmfree/common/utils/timestampUtils'

/**
 * 거래내역의 매칭 정보를 업데이트 (별도 export - 매칭 서비스용)
 * 날짜별로 번들링된 거래내역에서 해당 거래를 찾아 matchInfo 업데이트
 */
export async function updateBankTransactionMatchInfo(
  companyId: string,
  transactionId: string,
  matchInfo: { orderIds: string[]; dateMatched: Timestamp } | null
): Promise<ApiResponse<void>> {
  try {
    const db = getFirebaseDb()

    // 최근 30일 거래내역 문서를 가져와서 해당 거래 찾기
    for (let i = 0; i < 30; i++) {
      const date = new Date()
      date.setDate(date.getDate() - i)
      const kstDate = new Date(date.getTime() + 9 * 60 * 60 * 1000)
      const dateString = kstDate.toISOString().split('T')[0]

      const docRef = doc(db, 'v2_companies', companyId, 'v2_bank_transactions', dateString)
      const docSnap = await getDoc(docRef)

      if (docSnap.exists()) {
        const docData = docSnap.data()
        if (docData.transactions && Array.isArray(docData.transactions)) {
          const transactionIndex = docData.transactions.findIndex(
            (t: BankTransaction) => t.id === transactionId
          )

          if (transactionIndex !== -1) {
            // 거래 찾음 - matchInfo 업데이트
            if (matchInfo) {
              docData.transactions[transactionIndex].matchInfo = matchInfo
            } else {
              delete docData.transactions[transactionIndex].matchInfo
            }

            // 문서 업데이트
            await setDoc(docRef, {
              transactions: docData.transactions,
              dateUpdated: Timestamp.fromDate(new Date()),
            })

            // Company 버전 정보 업데이트 (0.0001 증가)
            await updateCompanyVersion(companyId, 'bankTransactionVersion', 0.0001)

            return {
              isSuccess: true,
              message: '거래내역 매칭 정보 업데이트 성공',
            }
          }
        }
      }
    }

    return {
      isSuccess: false,
      message: '해당 거래내역을 찾을 수 없습니다.',
    }
  } catch (error: any) {
    console.error('거래내역 매칭 정보 업데이트 실패:', error)
    return {
      isSuccess: false,
      message: error.message || '거래내역 매칭 정보 업데이트 중 오류가 발생했습니다.',
    }
  }
}

export function createBankTransactionService() {
  async function getModified(
    companyId: string,
    since: number
  ): Promise<ApiResponse<BankTransaction[]>> {
    try {
      const db = getFirebaseDb()
      const transactionsRef = collection(db, 'v2_companies', companyId, 'v2_bank_transactions')
      const sinceDate = Timestamp.fromMillis(since)

      const q = query(transactionsRef, where('dateUpdated', '>', sinceDate))
      const snapshot = await getDocs(q)

      const allTransactions: BankTransaction[] = []
      snapshot.forEach((docSnap) => {
        const data = docSnap.data()
        if (data.transactions && Array.isArray(data.transactions)) {
          allTransactions.push(...data.transactions)
        }
      })

      return {
        isSuccess: true,
        data: allTransactions,
        message: '거래내역 조회 성공',
      }
    } catch (error: any) {
      console.error('거래내역 조회 실패:', error)
      return {
        isSuccess: false,
        message: error.message || '거래내역 조회 중 오류가 발생했습니다.',
      }
    }
  }

  async function getDeleted(_companyId: string): Promise<ApiResponse<string[]>> {
    return {
      isSuccess: true,
      data: [],
      message: '은행 거래내역은 삭제를 지원하지 않습니다.',
    }
  }

  async function saveItem(
    companyId: string,
    transaction: BankTransaction
  ): Promise<ApiResponse<{ id: string }>> {
    try {
      const db = getFirebaseDb()

      // 거래 날짜를 KST 기준으로 추출
      let dateString: string

      if (transaction.dateCreated) {
        const date = toDate(transaction.dateCreated)
        // KST로 변환 (UTC + 9시간)
        const kstDate = new Date(date.getTime() + 9 * 60 * 60 * 1000)
        dateString = kstDate.toISOString().split('T')[0]
      } else {
        // 날짜가 없는 경우 오늘 날짜 사용
        const kstDate = new Date(Date.now() + 9 * 60 * 60 * 1000)
        dateString = kstDate.toISOString().split('T')[0]
      }

      // 해당 날짜 문서 처리
      const docRef = doc(db, 'v2_companies', companyId, 'v2_bank_transactions', dateString)
      const docSnap = await getDoc(docRef)

      if (docSnap.exists()) {
        // 기존 문서가 있으면 transactions 배열에 추가
        const data = docSnap.data()
        const transactions = data.transactions || []

        // 중복 체크 (같은 ID가 이미 있으면 업데이트)
        const existingIndex = transactions.findIndex(
          (t: BankTransaction) => t.id === transaction.id
        )

        if (existingIndex >= 0) {
          // 기존 거래 업데이트
          transactions[existingIndex] = transaction
        } else {
          // 새 거래 추가
          transactions.push(transaction)
        }

        await setDoc(docRef, {
          transactions,
          dateUpdated: Timestamp.fromDate(new Date()),
        })
      } else {
        // 새 문서 생성
        await setDoc(docRef, {
          transactions: [transaction],
          dateUpdated: Timestamp.fromDate(new Date()),
        })
      }

      // Company 버전 정보 업데이트 (0.0001 증가)
      await updateCompanyVersion(companyId, 'bankTransactionVersion', 0.0001)

      return {
        isSuccess: true,
        data: { id: transaction.id },
        message: '거래내역 저장 성공',
      }
    } catch (error: any) {
      console.error('거래내역 저장 실패:', error)
      return {
        isSuccess: false,
        message: error.message || '거래내역 저장 중 오류가 발생했습니다.',
      }
    }
  }

  async function getAll(companyId: string): Promise<ApiResponse<BankTransaction[]>> {
    try {
      const db = getFirebaseDb()
      const allTransactions: BankTransaction[] = []

      for (let i = 0; i < 30; i++) {
        const date = new Date()
        date.setDate(date.getDate() - i)
        const kstDate = new Date(date.getTime() + 9 * 60 * 60 * 1000)
        const dateString = kstDate.toISOString().split('T')[0]

        const docRef = doc(db, 'v2_companies', companyId, 'v2_bank_transactions', dateString)
        const docSnap = await getDoc(docRef)

        if (docSnap.exists()) {
          const data = docSnap.data()
          if (data.transactions && Array.isArray(data.transactions)) {
            allTransactions.push(...data.transactions)
          }
        }
      }

      return {
        isSuccess: true,
        data: allTransactions,
        message: '전체 거래내역 조회 성공',
      }
    } catch (error: any) {
      console.error('전체 거래내역 조회 실패:', error)
      return {
        isSuccess: false,
        message: error.message || '전체 거래내역 조회 중 오류가 발생했습니다.',
      }
    }
  }

  return {
    getModified,
    getDeleted,
    saveItem,
    getAll,
    // updateTransactionMatchInfo는 별도 export로 제공
  }
}
